import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import GenericSearchBar from '../../components/GenericSearchBar';
import Pagination from '../../components/shared/Pagination';
import BreadCrumb from '../../components/Breadcrumbs';
import { BlogDataProps } from './IBlogSearchPage';
import { currentUrl, hostName } from '../../helpers/projectConstant';
import BlogSideCards from '../../components/BlogSideCards';
import Container from '../../components/ui/Container';
import { Message } from '../../components/ui/Message';
import DefaultLayout from '../../layouts/DefaultLayout';

export interface BlogSearchPageProps {
  data: BlogDataProps[];
}

export const BlogSearchPage: React.FC<BlogSearchPageProps> = ({ data }) => {
  const [currentPageData, setCurrentPageData] = useState<BlogDataProps[]>(data);
  const [initialSearch, setInitialSearch] = useState('');
  const routeUrl = 'blogs/overzicht';
  const routeQuery = '?zoektermen=';

  const breadCrumbItems = [
    {
      name: 'Home',
      linkTo: '/',
    },
    {
      name: 'Blogs',
      linkTo: `${hostName}blogs`,
    },
    {
      name: 'Zoeken',
      linkTo: `${currentUrl}`,
    },
  ];

  const handleSearchClick = async (query: string): Promise<void> => {
    const encodedQuery = encodeURI(query);
    window.location.href = `${hostName}${routeUrl}${routeQuery}${encodedQuery}`;
  };
  const itemsPerPage = 10;
  const handlePageChange = (page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setCurrentPageData(data.slice(startIndex, endIndex));
  };

  useEffect(() => {
    setCurrentPageData(data.slice(0, itemsPerPage - 1));

    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    const entries = urlParams.entries();

    let result = entries.next();
    while (!result.done) {
      const entry = result.value;
      if (entry[0] === 'zoektermen') {
        setInitialSearch(decodeURI(entry[1]));
      }
      result = entries.next();
    }
  }, []);
  return (
    <DefaultLayout breadcrumbs={<BreadCrumb links={breadCrumbItems} />}>
      <Container state="defaultLayoutFirst" className="mb-16 md:mb-24">
        <div className="w-full flex justify-center lg:w-fit mb-8">
          <GenericSearchBar
            searchClicked={handleSearchClick}
            initialValue={initialSearch}
            title={'Vind een blog'}
            placeholder={'Zoeken op onderwerp'}
          />
        </div>

        <div className="flex flex-row flex-wrap -mx-2.5">
          <div className="w-full lg:w-[calc(66.6666%-1.25rem)] mx-2.5">
            <h1 className="text-xl font-bold text-blue-700 font-avantgarde mb-3">
              <span
                data-testid="blog-search-count"
                className="text-blue-500"
              >
                {data.length}
              </span>{' '}

              {initialSearch ? 'zoekresultaten gevonden voor' : 'blogs gevonden'}
              &nbsp;
              {initialSearch && <span className="font-normal">“{initialSearch}”</span>}
            </h1>
            <div className="text-stone-800 pb-5">
              Op deze pagina vind je {!initialSearch ? 'alle' : 'de'} blogs van dienstverleners die graag hun
              kennis via Advieskeuze delen.
            </div>
            <div>
              {currentPageData.length > 0 ? (
                <ul className="mt-2 font-normal text-sm">
                  {currentPageData.map((item) => (
                    <li
                      key={item.blogId}
                      className="mb-2"
                    >
                      <a
                        className="bg-white border border-solid border-stone-200 shadow-sm px-3 py-4 rounded-[8px] font-bold font-roboto no-underline text-blue-600 flex gap-3 items-center"
                        href={`/blogs/lezen/${item.blogSlug}`}
                      >
                        <FontAwesomeIcon
                          className="mr-2"
                          icon={faChevronRight as IconProp}
                          color={'#1081D3'}
                        />
                        {item.blogTitle}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <Message>
                  Geen resultaten gevonden voor <span className="font-bold">{initialSearch}</span>
                </Message>
              )}
            </div>
            {data.length !== 0 && (currentPageData.length) <= itemsPerPage && (
              <div className="mt-5 mb-5">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={data.length}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
          <div className="w-full lg:w-[calc(33.3333%-1.25rem)] mx-2.5">
            <BlogSideCards />
          </div>
        </div>
      </Container>
    </DefaultLayout>
  );
};

BlogSearchPage.displayName = 'Page:BlogSearch';

export default BlogSearchPage;
